import Loading from "@app/components/Loading";
import { useSetToast } from "@app/components/Toast";
import { QuestionStatusMultiSelect } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/QuestionsTab/components/QuestionStatusMultiSelect";
import { QuestionsWrapper } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/QuestionsTab/components/QuestionsWrapper";
import { QuestionnaireSidebar } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/QuestionsTab/components/Sidebar";
import { useCloseQuestionnaire } from "@app/screens/Supplier/Suppliers/supplier/data";
import { HeaderFullSkeleton } from "@app/shared/components/BackgroundedHeader";
import { Banner } from "@app/shared/components/Banner";
import { SupportedLocales } from "@app/shared/utils/locales";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { PageContainer } from "@design-system/Layout/PageContainer";
import {
    QuestionnaireStatus,
    ResponseStatus,
    type AnswerStatus,
} from "@generated/client/graphql";
import { useEffect, useMemo, useState, type FC } from "react";
import { useParams } from "react-router-dom";
import { QuestionnaireHeader } from "./components/Header";
import { Question } from "./components/Question";
import { QuestionProofFilter } from "./components/QuestionProofFilter";
import { SendQuestionnaireModal } from "./components/SendQuestionnaireModal";
import {
    useSupplierQuestionnaire,
    useSupplierQuestionnaireGroups,
    useSupplierQuestionnaireQuestions,
} from "./data";
import { useSupplierQuestionnaireTranslations } from "./i18n";
import { defaultStats, makeGroup } from "./services";
import { useQuestionnaireBreadcrumb } from "./useQuestionnnaireBreadcrumb";

export const Questionnaire: FC = () => {
    useQuestionnaireBreadcrumb();
    const { t, keys } = useSupplierQuestionnaireTranslations();
    const { questionnaire_id, sup_id } = useParams();
    const { questionnaire, isPending: isQuestionnairePending } =
        useSupplierQuestionnaire(questionnaire_id, sup_id);
    const [currentLocale, setCurrentLocale] = useCurrentLocale(
        questionnaire?.locale,
    );
    const isQuestionnaireOpen =
        questionnaire?.status === QuestionnaireStatus.Open;

    const [answerStatuses, setAnswerStatuses] = useState<AnswerStatus[]>([]);
    const [isProofFilterActive, setIsProofFilterActive] = useState(false);

    const { groups: groupsData, isPending: isGroupsPending } =
        useSupplierQuestionnaireGroups(questionnaire_id, sup_id);
    const [currentGroupId, setCurrentGroupId] = useState<string | undefined>(
        groupsData?.find((g) => g.level === 1)?.id,
    );
    const groups = useMemo(
        () => groupsData?.map((g) => makeGroup(g, currentLocale)) ?? [],
        [groupsData, currentLocale],
    );

    const currentGroupName = useMemo(
        () => groups?.find((g) => g.id === currentGroupId)?.name,
        [groups, currentGroupId],
    );

    const {
        questionList: { questions, missingProofQuestionsCount },
        isPending: isQuestionsPending,
    } = useSupplierQuestionnaireQuestions(questionnaire_id, sup_id, {
        answerStatus: answerStatuses,
        proofStatus: isProofFilterActive ? "missing" : undefined,
        questionGroupId: currentGroupId,
    });

    const notAnsweredQuestionsCount = useMemo(() => {
        return (
            (questionnaire?.stats?.total?.count ?? 0) -
            (questionnaire?.stats?.answered?.count ?? 0)
        );
    }, [questionnaire?.stats]);

    const notValidatedQuestionsCount = useMemo(() => {
        return (
            (questionnaire?.stats?.total?.count ?? 0) -
            (questionnaire?.stats?.validated?.count ?? 0)
        );
    }, [questionnaire?.stats]);

    const isFilterActive =
        answerStatuses.length > 0 ||
        isProofFilterActive ||
        currentGroupId !== undefined;

    const { closeQuestionnaire, isPending: isCloseQuestionnairePending } =
        useCloseQuestionnaire();

    const { setToastSuccess, setToastError } = useSetToast();

    const handleSendQuestionnaire = () => {
        if (!questionnaire_id) {
            return;
        }
        closeQuestionnaire(questionnaire_id, {
            onSettled(data, error) {
                if (
                    error ||
                    data?.closeQuestionnaire?.status !== ResponseStatus.Success
                ) {
                    setToastError(t(keys.send_questionnaire_toast_error));
                    return;
                }
                setToastSuccess(t(keys.send_questionnaire_toast_success));
            },
        });
    };

    useEffect(() => {
        if (groups.length > 0 && !currentGroupId) {
            setCurrentGroupId(groups[0]?.id);
        }
    }, [groups, currentGroupId]);

    return (
        <PageContainer className="pb-0">
            <FlexCol
                w="full"
                className="overflow-y-auto"
                style={
                    isQuestionnaireOpen
                        ? { maxHeight: "calc(100vh - 170px)" }
                        : {}
                }
            >
                <FlexCol p="4" gap="4">
                    {isQuestionnairePending ? (
                        <HeaderFullSkeleton />
                    ) : (
                        <QuestionnaireHeader
                            title={questionnaire?.name ?? ""}
                            stats={questionnaire?.stats ?? defaultStats}
                            locale={{
                                currentLocale,
                                onLocaleChange: setCurrentLocale,
                                availableLocales:
                                    questionnaire?.locale.availableLocales ??
                                    [],
                            }}
                            isQuestionnaireOpen={isQuestionnaireOpen}
                            deadline={questionnaire?.deadline}
                        />
                    )}
                </FlexCol>
                <Banner
                    className="sticky top-0 z-10 mb-4"
                    show={!isQuestionnairePending && !isQuestionnaireOpen}
                >
                    <Banner.Text>
                        {t(keys.banner_questionnaire_closed)}
                    </Banner.Text>
                </Banner>
                <FlexCol px="4" pb="4" gap="4">
                    <FlexRow alignItems="center" justifyContent="between">
                        <FlexRow gap="5" alignItems="center">
                            <QuestionStatusMultiSelect
                                value={answerStatuses}
                                onValueChange={setAnswerStatuses}
                                statusType="answer"
                                triggerLabel={t(keys.status_filter)}
                            />
                            <QuestionProofFilter
                                checked={isProofFilterActive}
                                onChange={setIsProofFilterActive}
                            />
                        </FlexRow>
                    </FlexRow>
                    <FlexRow gap="4" h="full" w="full">
                        <QuestionnaireSidebar
                            groups={groups}
                            activeGroupId={currentGroupId}
                            loading={isGroupsPending}
                            onGroupChange={setCurrentGroupId}
                        />
                        <QuestionsWrapper>
                            {currentGroupName && (
                                <QuestionsWrapper.GroupTitle>
                                    {currentGroupName}
                                </QuestionsWrapper.GroupTitle>
                            )}
                            <QuestionsWrapper.Group>
                                {isQuestionsPending ? (
                                    <Loading size="large" />
                                ) : questions.length > 0 ? (
                                    questions.map((question) => (
                                        <Question
                                            key={question.id}
                                            currentLocale={currentLocale}
                                            question={question}
                                            isOpen={isQuestionnaireOpen}
                                        />
                                    ))
                                ) : isFilterActive ? (
                                    <QuestionsWrapper.EmptyGroup />
                                ) : null}
                            </QuestionsWrapper.Group>
                        </QuestionsWrapper>
                    </FlexRow>
                </FlexCol>
            </FlexCol>
            {isQuestionnaireOpen && (
                <FlexRow
                    w="full"
                    px="4"
                    pt="2"
                    alignItems="center"
                    justifyContent="end"
                >
                    <SendQuestionnaireModal
                        notAnsweredQuestionsCount={notAnsweredQuestionsCount}
                        notValidatedQuestionsCount={notValidatedQuestionsCount}
                        missingProofQuestionsCount={missingProofQuestionsCount}
                        loading={isCloseQuestionnairePending}
                        onConfirm={handleSendQuestionnaire}
                    />
                </FlexRow>
            )}
        </PageContainer>
    );
};

function useCurrentLocale({
    defaultLocale = SupportedLocales.EN,
    availableLocales = [SupportedLocales.EN, SupportedLocales.FR],
}: {
    defaultLocale?: SupportedLocales;
    availableLocales?: SupportedLocales[];
} = {}) {
    const [currentLocale, setCurrentLocale] = useState<SupportedLocales>(
        defaultLocale ?? availableLocales?.[0] ?? SupportedLocales.EN,
    );

    useEffect(() => {
        if (!availableLocales.includes(currentLocale)) {
            setCurrentLocale(defaultLocale);
        }
    }, [availableLocales, currentLocale, defaultLocale]);

    return [currentLocale, setCurrentLocale] as const;
}
